import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

/*
 * public/index.html ships a set of fallback head tags marked with
 * data-seo-fallback so crawlers that don't execute JavaScript (WhatsApp,
 * LinkedIn, Slack link previews) still get a title, description and Open Graph
 * card. They describe the homepage.
 *
 * react-helmet-async v3 inserts its own tags without claiming those, which
 * would leave two of each in the rendered DOM — and on any non-home route the
 * fallback canonical would point at "/", which Google treats as a conflicting
 * signal. So once React has mounted and Helmet owns the head, drop them.
 */
let fallbacksRemoved = false;

function useRemoveStaticFallbacks() {
  useEffect(() => {
    if (fallbacksRemoved) return;
    fallbacksRemoved = true;
    document
      .querySelectorAll('head [data-seo-fallback="true"]')
      .forEach(el => el.parentNode.removeChild(el));
  }, []);
}

/*
 * Per-page <head> tags: title, meta description, and a self-referencing
 * canonical on the www host. Open Graph / Twitter tags are kept in sync with
 * the same values so they can't drift apart.
 *
 * `path` must start with "/" and match the route exactly (e.g. "/locations").
 * Extra tags — JSON-LD, for instance — can be passed as children.
 */

export const ORIGIN = 'https://www.mysterymansion.in';

export default function Seo({ title, description, path, image, children }) {
  useRemoveStaticFallbacks();

  const canonical = `${ORIGIN}${path}`;
  const preview = image || `${ORIGIN}/preview_new.png?v=1`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />

      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={preview} />

      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonical} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={preview} />

      {children}
    </Helmet>
  );
}
